import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Phone, PhoneCall, MessageSquare, Trophy, Target, AlertCircle, Settings2 } from "lucide-react";
import { FieldMappingDialog } from "./FieldMappingDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter145 — Role-tailored leaderboard.
 *
 * Reads server-supplied column metadata (`/api/leaderboard/by-role/<role>`)
 * so adding new role columns later is a one-side change: tweak the
 * Python `_columns_for_role()` and the JSX renders the new layout
 * automatically.
 *
 * Three preset roles: closer, phone_setter, dm_setter. The selected
 * role tab persists in localStorage so reps land on "their" board next
 * time.
 *
 * Iter149 — props `startDate` / `endDate` thread the page-level date
 * picker into the API call (previously the by-role tab defaulted to
 * "last 30 days" regardless of what the manager had picked). Also
 * surfaces the `needs_field_mapping` row flag with a one-click open of
 * the Field Mapping admin dialog — keeps the close-% fix modular.
 */
// Iter233 — `endpoint` overrides the by-role URL; `fixedWindow` tabs ignore
// the page date picker (server picks the window).
const ROLES = [
  { key: "closer", label: "Closers", icon: Trophy },
  { key: "phone_setter", label: "Phone Setters", icon: Phone },
  { key: "dialing", label: "Dialing", icon: PhoneCall, endpoint: "/leaderboard/dialing" },
  { key: "dm_setter", label: "DM Setters", icon: MessageSquare },
  {
    key: "dm_close", label: "DM Close · 7d", icon: Target,
    endpoint: "/leaderboard/dm-close-weekly", fixedWindow: true,
    note: "Rolling last 7 days, ranked by Pitch → DM Close %. Ignores the date picker.",
  },
];

const LS_KEY = "warmap.leaderboard.activeRole";

function formatCell(value, format) {
  if (value == null) return "—";
  switch (format) {
    case "percent": {
      const n = Number(value);
      return Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";
    }
    case "currency": {
      const n = Number(value);
      return Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—";
    }
    case "int": {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString() : "—";
    }
    default: return String(value);
  }
}

function initials(name) {
  return (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

// Iter229 — "good/bad" traffic-light coloring for rate & cash-ratio columns,
// matching the user-provided reference leaderboard screenshot style.
//
// Two coloring strategies, driven by the server-supplied `col.colorize`:
//   - "higher_better" / "lower_better": ABSOLUTE thresholds on a 0-100
//     percent scale (≥50% green / 30-49% amber / <30% red, inverted for
//     "lower_better" metrics like DQ % where a LOW number is good).
//   - "relative": currency-ratio columns (Cash/Call, AOV, etc.) have no
//     universal dollar threshold — colored by PERCENTILE RANK within the
//     column across the currently-visible rows (top third green, bottom
//     third red, middle amber).
const TIER_CLASS = {
  green: "text-emerald-400",
  amber: "text-amber-300",
  red: "text-red-400",
};

// Iter231 — `good`/`bad` default to the fixed 50/30 cutoffs, but are
// overridable via manager-configured thresholds fetched from
// GET /api/leaderboard/thresholds (Settings → Leaderboard Thresholds).
function absoluteTier(colorize, value, good = 50, bad = 30) {
  if (!Number.isFinite(value)) return null;
  if (colorize === "higher_better") {
    if (value >= good) return "green";
    if (value < bad) return "red";
    return "amber";
  }
  if (colorize === "lower_better") {
    if (value < good) return "green";
    if (value >= bad) return "red";
    return "amber";
  }
  return null;
}

function relativeTier(values, value) {
  if (!Number.isFinite(value) || values.length < 2) return null;
  // No real variance to rank against (e.g. every rep shows $0 cash/call
  // because no deals landed yet this period) — stay neutral, not green.
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return null;
  const countLE = values.filter((v) => v <= value).length;
  const percentile = countLE / values.length;
  if (percentile >= 0.67) return "green";
  if (percentile <= 0.33) return "red";
  return "amber";
}

export function RoleLeaderboard({ startDate, endDate, isManager = false }) {
  const [activeRole, setActiveRole] = useState(() => {
    try { return window.localStorage.getItem(LS_KEY) || "closer"; }
    catch { return "closer"; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [focusField, setFocusField] = useState(null);
  // Iter231 — manager-tunable good/bad cutoffs per metric key, fetched
  // once (doesn't depend on role/date) and merged over the absoluteTier()
  // hardcoded 50/30 defaults.
  const [thresholds, setThresholds] = useState({});

  // Iter150 — auto-open the Field Mapping dialog when a manager arrives
  // via the in-app notification link `/leaderboard?open_field_mapping=1`.
  // The notification is fired by the EOD field notifier when a brand
  // new custom EOD field is detected for the first time.
  //
  // Iter154 — also support `&focus_field=q_XXXXX` so the "Map me" link
  // inside the template editor can deep-link to a specific row.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("open_field_mapping") === "1" && isManager) {
      setMappingOpen(true);
      const focus = searchParams.get("focus_field");
      if (focus) setFocusField(focus);
      // Strip the params so a manual refresh doesn't reopen the dialog.
      const next = new URLSearchParams(searchParams);
      next.delete("open_field_mapping");
      next.delete("focus_field");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, isManager, setSearchParams]);

  // Iter231 — fetch manager-configured thresholds once on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/leaderboard/thresholds`, { credentials: "include" });
        if (!res.ok) return;
        const list = await res.json();
        const byKey = {};
        for (const t of list) byKey[t.key] = { good: t.good, bad: t.bad };
        setThresholds(byKey);
      } catch { /* fall back to absoluteTier() hardcoded defaults */ }
    })();
  }, []);

  // Format date props for the API. `startDate` / `endDate` are Date
  // objects from the parent picker; the backend accepts ISO YYYY-MM-DD.
  const startParam = startDate ? format(startDate, "yyyy-MM-dd") : null;
  const endParam = endDate ? format(endDate, "yyyy-MM-dd") : null;

  useEffect(() => {
    try { window.localStorage.setItem(LS_KEY, activeRole); } catch { /* ignore */ }
    setLoading(true);
    (async () => {
      try {
        const role = ROLES.find((r) => r.key === activeRole) || ROLES[0];
        const qs = new URLSearchParams();
        if (!role.fixedWindow && startParam) qs.set("start_date", startParam);
        if (!role.fixedWindow && endParam) qs.set("end_date", endParam);
        const path = role.endpoint || `/leaderboard/by-role/${role.key}`;
        const url = `${API}${path}${qs.toString() ? `?${qs}` : ""}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          setData(null);
          return;
        }
        setData(await res.json());
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeRole, startParam, endParam]);

  // Iter229 — precompute the numeric value pool for every "relative"
  // colorize column (currency ratios with no universal $ threshold), so
  // each cell can be ranked by percentile against its peers on this board.
  const relativeColumnValues = useMemo(() => {
    const pool = {};
    if (!data?.columns || !data?.rows) return pool;
    for (const col of data.columns) {
      if (col.colorize !== "relative") continue;
      pool[col.key] = data.rows
        .map((r) => Number(r[col.key]))
        .filter((v) => Number.isFinite(v));
    }
    return pool;
  }, [data]);

  return (
    <div className="space-y-4" data-testid="role-leaderboard">
      {/* Role tabs */}
      <div className="flex items-center justify-between gap-2 border-b border-white/5">
        <div className="flex items-center gap-2">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = activeRole === r.key;
            return (
              <button
                key={r.key}
                onClick={() => setActiveRole(r.key)}
                data-testid={`role-tab-${r.key}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-2 ${
                  active
                    ? "border-emerald-500 text-emerald-300"
                    : "border-transparent text-gray-400 hover:text-gray-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                {r.label}
              </button>
            );
          })}
        </div>
        {/* Iter149 — modular admin entry point: lets managers map any
            custom EOD field to a canonical column (Shows, Deals, Cash…)
            without leaving the Leaderboard. Persists per-account via
            `eod_field_mappings`; takes effect on next page load. */}
        {isManager && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMappingOpen(true)}
            data-testid="open-field-mapping-btn"
            className="text-gray-400 hover:text-emerald-300 hover:bg-emerald-500/10"
            title="Map custom EOD fields to leaderboard columns"
          >
            <Settings2 className="w-4 h-4 mr-1.5" />
            Map EOD Fields
          </Button>
        )}
      </div>

      {ROLES.find((r) => r.key === activeRole)?.note && (
        <p className="text-xs text-gray-400" data-testid="role-leaderboard-note">
          {ROLES.find((r) => r.key === activeRole).note}
        </p>
      )}

      {/* Table */}
      <div className="bg-[#1A1A1A] border border-white/5 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Loading {ROLES.find((r) => r.key === activeRole)?.label?.toLowerCase()}…
          </div>
        ) : !data || !data.rows?.length ? (
          <div className="p-10 text-center text-gray-500" data-testid="role-leaderboard-empty">
            No {ROLES.find((r) => r.key === activeRole)?.label?.toLowerCase()} on the board yet for this period.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-white/5">
                <TableHead className="text-gray-500 w-12">#</TableHead>
                {data.columns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={`text-gray-500 ${col.format === "rep" ? "" : "text-right"} ${col.primary ? "text-emerald-400" : ""}`}
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row, idx) => (
                <TableRow
                  key={row.user_id}
                  className="border-white/5 hover:bg-white/[0.02]"
                  data-testid={`role-leaderboard-row-${row.user_id}`}
                >
                  <TableCell className="text-gray-500 font-mono">
                    {idx + 1}
                  </TableCell>
                  {data.columns.map((col) => {
                    if (col.format === "rep") {
                      return (
                        <TableCell key={col.key} className="text-white">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="w-7 h-7">
                              {row.picture && <AvatarImage src={row.picture} alt={row.name} />}
                              <AvatarFallback className="bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold">
                                {initials(row.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate">{row.name}</span>
                          </div>
                        </TableCell>
                      );
                    }
                    // Iter149 / Iter176 — Close % cell surfaces a
                    // prominent "Fix mapping" pill (managers only) when
                    // the server flags this row as `needs_field_mapping`
                    // — i.e. the rep has deals but ZERO mapped shows,
                    // meaning their custom EOD template's "shows-
                    // equivalent" field isn't wired to the canonical
                    // `shows` column. Clicking opens the Field Mapping
                    // dialog so the manager can map it in one place;
                    // close_rate returns as 0 (not a fake 100%) until
                    // the mapping is fixed.
                    const isCloseRateCell = col.key === "close_rate";
                    const showFixMappingBadge =
                      isCloseRateCell && isManager && row.needs_field_mapping === true;
                    // Iter229 — red/amber/green traffic-light coloring on
                    // rate + cash-ratio columns (see `absoluteTier` /
                    // `relativeTier` above).
                    let tier = null;
                    if (col.colorize === "relative") {
                      tier = relativeTier(relativeColumnValues[col.key] || [], Number(row[col.key]));
                    } else if (col.colorize === "higher_better" || col.colorize === "lower_better") {
                      const t = thresholds[col.key];
                      tier = absoluteTier(col.colorize, Number(row[col.key]), t?.good, t?.bad);
                    }
                    const cellColorClass = tier
                      ? TIER_CLASS[tier]
                      : col.primary ? "text-emerald-300 font-semibold" : "text-white";
                    return (
                      <TableCell
                        key={col.key}
                        data-testid={tier ? `leaderboard-cell-${col.key}-${tier}-${row.user_id}` : undefined}
                        className={`text-right font-mono tabular-nums ${cellColorClass} ${tier ? "font-semibold" : ""}`}
                      >
                        <div className="inline-flex items-center gap-2 justify-end">
                          {formatCell(row[col.key], col.format)}
                          {showFixMappingBadge && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMappingOpen(true);
                              }}
                              data-testid={`fix-mapping-btn-${row.user_id}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 hover:text-amber-200 transition-colors cursor-pointer"
                              title={`${row.name} has deals but no mapped shows — click to map their custom EOD "shows" field so close-% is accurate.`}
                            >
                              <AlertCircle className="w-3 h-3" />
                              Fix mapping
                            </button>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {data?.period && (
        <p className="text-[10px] text-gray-500 text-right" data-testid="role-leaderboard-period">
          Showing {data.period.start} → {data.period.end} · {data.rows?.length || 0} reps
        </p>
      )}

      {/* Iter149 — modular admin tool. Mounted here so the warning chip
          and the header button share the same dialog instance. */}
      <FieldMappingDialog
        open={mappingOpen}
        onOpenChange={(o) => {
          setMappingOpen(o);
          if (!o) setFocusField(null);
        }}
        focusField={focusField}
      />
    </div>
  );
}
